// src/pages/CreateCohort.tsx
import React from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { auth, db } from "../firebase";
import {
  ref,
  get,
  child,
  push,
  update,
  remove,
  onValue,
} from "firebase/database";
import {
  CATALOG_SUBJECTS_FIXED,
  CATALOG_PROCEDURES_FIXED,
} from "../catalog.fixed";

type Group = {
  id: string;
  name: string;
  createdAt?: string;
  members?: Record<string, true>;
  procedures?: Record<string, true>;
  assignedProcedures?: Record<string, true>;
};

type Student = { uid: string; name?: string; email?: string };
type CatalogSubject = { id: string; displayName: string; order: number };
type CatalogProcedure = {
  id: string;
  displayName: string;
  subjectId: string;
  legacyAliases: string[];
  active: boolean;
};

function buildNameToIdIndex(procs: CatalogProcedure[]) {
  const map: Record<string, string> = {};
  procs.forEach((p) => {
    map[p.displayName.trim().toLowerCase()] = p.id;
    p.legacyAliases.forEach((alias) => {
      const key = alias.trim().toLowerCase();
      if (key) map[key] = p.id;
    });
  });
  return map;
}

function toTitle(s: string) {
  return s
    .toLowerCase()
    .replace(/\s+/g, " ")
    .trim()
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

function GroupCard({
  g,
  students,
  subjects,
  procsBySubject,
  nameToId,
  onRename,
  onDelete,
  onSaveAssigned,
  onAddMemberByEmail,
  onRemoveMember,
  onOpenAnalytics,
}: {
  g: Group;
  students: Student[];
  subjects: CatalogSubject[];
  procsBySubject: Record<string, CatalogProcedure[]>;
  nameToId: Record<string, string>;
  onRename: (id: string, name: string) => void;
  onDelete: (id: string) => void;
  onSaveAssigned: (id: string, procIds: string[]) => void;
  onAddMemberByEmail: (id: string, email: string) => void;
  onRemoveMember: (id: string, uid: string) => void;
  onOpenAnalytics: (id: string) => void;
}) {
  const [subjectId, setSubjectId] = React.useState(
    subjects.length ? subjects[0].id : ""
  );
  const [emailToAdd, setEmailToAdd] = React.useState("");

  const initialIds = React.useMemo(() => {
    if (g.assignedProcedures && Object.keys(g.assignedProcedures).length) {
      return Object.keys(g.assignedProcedures);
    }
    if (g.procedures) {
      const ids = Object.keys(g.procedures)
        .map((raw) => {
          const lower = raw.trim().toLowerCase();
          return (
            nameToId[lower] || nameToId[toTitle(lower).toLowerCase()] || null
          );
        })
        .filter(Boolean) as string[];
      return ids;
    }
    return [];
  }, [g.assignedProcedures, g.procedures, nameToId]);

  const [selected, setSelected] = React.useState<Set<string>>(
    new Set(initialIds)
  );

  React.useEffect(() => {
    setSelected(new Set(initialIds));
  }, [initialIds.join("|")]);

  React.useEffect(() => {
    if (subjectId && !subjects.find((s) => s.id === subjectId)) {
      setSubjectId(subjects.length ? subjects[0].id : "");
    }
  }, [subjects, subjectId]);

  const memberList = Object.keys(g.members || {});
  const byUid: Record<string, Student> = {};
  students.forEach((s) => (byUid[s.uid] = s));

  const procsForSubject = procsBySubject[subjectId] || [];

  function toggle(pid: string) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(pid)) next.delete(pid);
      else next.add(pid);
      return next;
    });
  }

  const selectedNames = React.useMemo(() => {
    const all = Object.values(procsBySubject).flat();
    const nameById: Record<string, string> = {};
    all.forEach((p) => (nameById[p.id] = p.displayName));
    return Array.from(selected).map((id) => nameById[id] || id);
  }, [selected, procsBySubject]);

  return (
    <div className="card" style={{ marginBottom: 12 }}>
      <div className="row">
        <h3 style={{ margin: 0 }}>{g.name}</h3>
        <div style={{ display: "flex", gap: 8 }}>
          <button className="btn" onClick={() => onOpenAnalytics(g.id)}>
            Analytics
          </button>
          <button
            className="btn"
            onClick={() => {
              const v = prompt("Rename cohort:", g.name);
              if (v != null) onRename(g.id, v);
            }}
          >
            Rename
          </button>
          <button className="btn" onClick={() => onDelete(g.id)}>
            Delete
          </button>
        </div>
      </div>
      <p className="small">Created: {g.createdAt || "—"}</p>

      {/* Procedures */}
      <div style={{ marginTop: 8 }}>
        <div className="row">
          <b>Assigned procedures</b>
        </div>
        <p className="small" style={{ marginTop: 4 }}>
          Pick a subject, tick procedures, then click <i>Save</i>.
        </p>

        <div className="row" style={{ marginTop: 6, gap: 8 }}>
          <select
            className="input"
            value={subjectId}
            onChange={(e) => setSubjectId(e.target.value)}
            style={{ maxWidth: 360 }}
          >
            {subjects.map((s) => (
              <option key={s.id} value={s.id}>
                {s.displayName}
              </option>
            ))}
          </select>

          <button
            className="btn"
            onClick={() => onSaveAssigned(g.id, Array.from(selected))}
          >
            Save
          </button>
        </div>

        <div
          className="card"
          style={{ marginTop: 10, padding: 10, background: "#0c1219" }}
        >
          {procsForSubject.length === 0 ? (
            <p className="small">No procedures in this subject.</p>
          ) : (
            <div
              style={{
                display: "grid",
                gap: 6,
                gridTemplateColumns: "repeat(auto-fill, minmax(240px, 1fr))",
              }}
            >
              {procsForSubject.map((p) => (
                <label
                  key={p.id}
                  className="small"
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                    padding: "6px 8px",
                    border: "1px solid var(--line)",
                    borderRadius: 8,
                  }}
                >
                  <input
                    type="checkbox"
                    checked={selected.has(p.id)}
                    onChange={() => toggle(p.id)}
                  />{" "}
                  {p.displayName}
                </label>
              ))}
            </div>
          )}
        </div>

        <p className="small" style={{ marginTop: 8 }}>
          Now: {selectedNames.length ? selectedNames.join(", ") : "None"}
        </p>
      </div>

      {/* Members (NO UID DISPLAY) */}
      <div style={{ marginTop: 12 }}>
        <div className="row">
          <b>Members</b>
        </div>

        <div className="row" style={{ marginTop: 6, gap: 8 }}>
          <input
            className="input"
            type="email"
            placeholder="student@email.com"
            onKeyDown={(e) => {
              // as-you-type convenience
              if (e.key === "Enter") {
                const val = (e.target as HTMLInputElement).value;
                (e.target as HTMLInputElement).value = "";
                onAddMemberByEmail(g.id, val);
              }
            }}
          />
          <button
            className="btn"
            onClick={() => {
              const el = document.querySelector<HTMLInputElement>(
                'input[type="email"].input'
              );
              const email = el?.value?.trim() || "";
              if (!email) return;
              el!.value = "";
              onAddMemberByEmail(g.id, email);
            }}
          >
            Add by email
          </button>
        </div>

        {memberList.length === 0 ? (
          <p className="small" style={{ marginTop: 6 }}>
            No members yet.
          </p>
        ) : (
          <table className="table" style={{ marginTop: 8 }}>
            <thead>
              <tr>
                <th>Name</th>
                <th>Email</th>
                <th>Remove</th>
              </tr>
            </thead>
            <tbody>
              {memberList.map((muid) => {
                const s = byUid[muid];
                return (
                  <tr key={muid}>
                    <td>{s?.name || "—"}</td>
                    <td>{s?.email || "—"}</td>
                    <td>
                      <button
                        className="btn"
                        onClick={() => onRemoveMember(g.id, muid)}
                      >
                        Remove
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

export default function CreateCohort() {
  const nav = useNavigate();
  const loc = useLocation() as any;
  const college: string =
    loc?.state?.college ||
    (sessionStorage.getItem("college") ?? "UNKNOWN_COLLEGE");

  const [groups, setGroups] = React.useState<Group[]>([]);
  const [students, setStudents] = React.useState<Student[]>([]);
  const [newName, setNewName] = React.useState("");

  const subjects = React.useMemo<CatalogSubject[]>(
    () => [...CATALOG_SUBJECTS_FIXED],
    []
  );
  const procedures = React.useMemo<CatalogProcedure[]>(
    () => CATALOG_PROCEDURES_FIXED.filter((p) => p.active !== false),
    []
  );
  const nameToId = React.useMemo(
    () => buildNameToIdIndex(procedures),
    [procedures]
  );
  const procsBySubject = React.useMemo(() => {
    const map: Record<string, CatalogProcedure[]> = {};
    procedures.forEach((p) => {
      if (!map[p.subjectId]) map[p.subjectId] = [];
      map[p.subjectId].push(p);
    });
    Object.keys(map).forEach((k) =>
      map[k].sort((a, b) => a.displayName.localeCompare(b.displayName))
    );
    return map;
  }, [procedures]);

  React.useEffect(() => {
    const gref = ref(db, `Colleges/${college}/Groups`);
    const unsub = onValue(gref, (snap) => {
      const v = snap.val() as Record<string, any> | null;
      if (!v) return setGroups([]);
      const arr: Group[] = Object.entries(v).map(([id, g]) => ({
        id,
        name: g?.name ?? "(untitled)",
        createdAt: g?.createdAt,
        members: g?.members || {},
        procedures: g?.procedures || {},
        assignedProcedures: g?.assignedProcedures || {},
      }));
      arr.sort((a, b) =>
        String(b.createdAt || "").localeCompare(String(a.createdAt || ""))
      );
      setGroups(arr);
    });
    return () => unsub();
  }, [college]);

  React.useEffect(() => {
    (async () => {
      const root = ref(db);
      const usersSnap = await get(child(root, `Colleges/${college}/Users`));
      if (!usersSnap.exists()) return setStudents([]);
      const users = usersSnap.val() as Record<string, any>;
      const arr: Student[] = Object.keys(users).map((uid) => {
        const pd = users[uid]?.["Personal Data"] || {};
        return { uid, name: pd?.userName || uid, email: pd?.userEmail };
      });
      setStudents(arr);
    })();
  }, [college]);

  async function createGroup() {
    const name = newName.trim();
    if (!name) return;
    const now = new Date().toISOString();
    const gref = ref(db, `Colleges/${college}/Groups`);
    await push(gref, {
      name,
      createdAt: now,
      members: {},
      procedures: {},
      assignedProcedures: {},
      createdBy: auth.currentUser?.uid || "faculty",
    });
    setNewName("");
  }
  async function renameGroup(id: string, name: string) {
    await update(ref(db, `Colleges/${college}/Groups/${id}`), { name });
  }
  async function deleteGroup(id: string) {
    if (!confirm("Delete this cohort?")) return;
    await remove(ref(db, `Colleges/${college}/Groups/${id}`));
  }
  async function saveAssignedProcedures(groupId: string, procIds: string[]) {
    const obj: Record<string, true> = {};
    procIds.forEach((id) => (obj[id] = true));
    await update(ref(db, `Colleges/${college}/Groups/${groupId}`), {
      assignedProcedures: procIds.length ? obj : null,
    });
  }
  async function addMemberByEmail(groupId: string, email: string) {
    const emailLower = email.trim().toLowerCase();
    if (!emailLower) return;
    const match = students.find(
      (s) => (s.email || "").trim().toLowerCase() === emailLower
    );
    if (!match) {
      alert("No student found with that email in this college.");
      return;
    }
    await update(ref(db, `Colleges/${college}/Groups/${groupId}/members`), {
      [match.uid]: true,
    });
  }
  async function removeMember(groupId: string, uid: string) {
    await remove(
      ref(db, `Colleges/${college}/Groups/${groupId}/members/${uid}`)
    );
  }

  return (
    <div className="container">
      <div className="row" style={{ marginBottom: 12 }}>
        <h2 style={{ margin: 0 }}>Create Cohort</h2>
      </div>

      <div className="card" style={{ marginBottom: 12 }}>
        <h3>Create a new cohort</h3>
        <div className="row">
          <input
            className="input"
            placeholder="Cohort name (e.g., MBBS Sem-3: Respiratory)"
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
          />
        </div>
        <div className="row" style={{ marginTop: 8 }}>
          <button className="btn primary" onClick={createGroup}>
            Create
          </button>
        </div>
      </div>

      {groups.length === 0 ? (
        <div className="card">
          <p className="small">No cohorts yet.</p>
        </div>
      ) : (
        groups.map((g) => (
          <GroupCard
            key={g.id}
            g={g}
            students={students}
            subjects={subjects}
            procsBySubject={procsBySubject}
            nameToId={nameToId}
            onRename={renameGroup}
            onDelete={deleteGroup}
            onSaveAssigned={saveAssignedProcedures}
            onAddMemberByEmail={addMemberByEmail}
            onRemoveMember={removeMember}
            onOpenAnalytics={(id) =>
              nav(`/f/groups/${id}`, { state: { college } })
            }
          />
        ))
      )}
    </div>
  );
}
